import { useState, useEffect, useRef } from 'react';

export default function StickyHeader() {
  const [activeSection, setActiveSection] = useState('hero');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    // Use IntersectionObserver for better performance with scroll snapping
    const observerOptions = {
      root: null,
      rootMargin: '-56px 0px -50% 0px', // Account for header height and require 50% visibility
      threshold: 0
    };

    observerRef.current = new IntersectionObserver((entries) => {
      // Find the section that's most visible
      let mostVisibleSection = '';
      let maxRatio = 0;

      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio > maxRatio) {
          maxRatio = entry.intersectionRatio;
          mostVisibleSection = entry.target.id;
        }
      });

      if (mostVisibleSection) {
        setActiveSection(mostVisibleSection);
      }
    }, observerOptions);

    // Observe all sections
    const sections = ['hero', 'about', 'art-therapy', 'testimonials', 'services-pricing', 'education', 'contact'];
    sections.forEach(sectionId => {
      const element = document.getElementById(sectionId);
      if (element && observerRef.current) {
        observerRef.current.observe(element);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      // Use native scrollIntoView for better snap behavior
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
    setIsMobileMenuOpen(false);
  };

  const scrollToBooking = () => {
    scrollToSection('services-pricing');
  };

  return (
    <>
      {/* Sticky Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#829BB8] shadow-lg border-b border-[#3D5C4A]">
        <div className="px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-14">
            {/* Left: Brand */}
            <div className="flex items-center gap-2 md:gap-3 min-w-0">
              <button
                onClick={() => scrollToSection('hero')}
                className="flex-shrink-0 w-10 h-12 md:w-14 md:h-12 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#D6794E] focus:ring-offset-2 focus:ring-offset-[#829BB8]"
                aria-label="Go to home"
              >
                <img
                  src="https://storage.helloreaddy.io/project_files/b4f3eeda-f893-4987-8921-4636c63c4dc6/48064541-945d-4db4-9b5f-090b501b738b_compressed_Logo-Symbol-White.webp"
                  alt="Yuvati Art logo"
                  className="w-full h-full object-contain"
                />
              </button>
              <div className="flex flex-col min-w-0">
                <p className="text-base md:text-lg font-normal leading-tight whitespace-nowrap" style={{ fontFamily: "'Belanosima', sans-serif", color: '#FBF7F0' }}>Yuliya Yuvati</p>
                <p className="text-[11px] md:text-xs font-normal leading-tight whitespace-nowrap" style={{ color: 'rgba(251, 247, 240, 0.8)' }}>Art Therapist & Counselor</p>
              </div>
            </div>

            {/* Right: Desktop Menu */}
            <nav className="hidden lg:flex items-center space-x-6" role="navigation" aria-label="Main">
              <button
                onClick={() => scrollToSection('about')}
                className={`relative px-2 py-1.5 text-sm font-normal transition-all duration-200 hover:text-[#D6794E] focus:outline-none focus:ring-2 focus:ring-[#D6794E] focus:ring-offset-2 focus:ring-offset-[#829BB8] rounded-xl ${
                  activeSection === 'about'
                    ? 'text-[#D6794E]'
                    : 'text-[#FBF7F0]/90'
                }`}
                aria-current={activeSection === 'about' ? 'page' : undefined}
              >
                About
                {activeSection === 'about' && (
                  <span className="absolute -bottom-0.5 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-[#D6794E] rounded-full"></span>
                )}
              </button>

              <button
                onClick={() => scrollToSection('art-therapy')}
                className={`relative px-2 py-1.5 text-sm font-normal transition-all duration-200 hover:text-[#D6794E] focus:outline-none focus:ring-2 focus:ring-[#D6794E] focus:ring-offset-2 focus:ring-offset-[#829BB8] rounded-xl ${
                  activeSection === 'art-therapy'
                    ? 'text-[#D6794E]'
                    : 'text-[#FBF7F0]/90'
                }`}
                aria-current={activeSection === 'art-therapy' ? 'page' : undefined}
              >
                Art Therapy
                {activeSection === 'art-therapy' && (
                  <span className="absolute -bottom-0.5 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-[#D6794E] rounded-full"></span>
                )}
              </button>

              <button
                onClick={() => scrollToSection('testimonials')}
                className={`relative px-2 py-1.5 text-sm font-normal transition-all duration-200 hover:text-[#D6794E] focus:outline-none focus:ring-2 focus:ring-[#D6794E] focus:ring-offset-2 focus:ring-offset-[#829BB8] rounded-xl ${
                  activeSection === 'testimonials'
                    ? 'text-[#D6794E]'
                    : 'text-[#FBF7F0]/90'
                }`}
                aria-current={activeSection === 'testimonials' ? 'page' : undefined}
              >
                Testimonials
                {activeSection === 'testimonials' && (
                  <span className="absolute -bottom-0.5 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-[#D6794E] rounded-full"></span>
                )}
              </button>

              <button
                onClick={() => scrollToSection('services-pricing')}
                className={`relative px-2 py-1.5 text-sm font-normal transition-all duration-200 hover:text-[#D6794E] focus:outline-none focus:ring-2 focus:ring-[#D6794E] focus:ring-offset-2 focus:ring-offset-[#829BB8] rounded-xl ${
                  activeSection === 'services-pricing'
                    ? 'text-[#D6794E]'
                    : 'text-[#FBF7F0]/90'
                }`}
                aria-current={activeSection === 'services-pricing' ? 'page' : undefined}
              >
                Services & Pricing
                {activeSection === 'services-pricing' && (
                  <span className="absolute -bottom-0.5 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-[#D6794E] rounded-full"></span>
                )}
              </button>

              <button
                onClick={() => scrollToSection('education')}
                className={`relative px-2 py-1.5 text-sm font-normal transition-all duration-200 hover:text-[#D6794E] focus:outline-none focus:ring-2 focus:ring-[#D6794E] focus:ring-offset-2 focus:ring-offset-[#829BB8] rounded-xl ${
                  activeSection === 'education'
                    ? 'text-[#D6794E]'
                    : 'text-[#FBF7F0]/90'
                }`}
                aria-current={activeSection === 'education' ? 'page' : undefined}
              >
                Education & Certification
                {activeSection === 'education' && (
                  <span className="absolute -bottom-0.5 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-[#D6794E] rounded-full"></span>
                )}
              </button>

              <a
                href="https://blog.yuvati.art"
                target="_blank"
                rel="noopener noreferrer"
                className="relative px-2 py-1.5 text-sm font-normal transition-all duration-200 hover:text-[#D6794E] focus:outline-none focus:ring-2 focus:ring-[#D6794E] focus:ring-offset-2 focus:ring-offset-[#829BB8] rounded-xl text-[#FBF7F0]/90 whitespace-nowrap"
              >
                Blog
              </a>

              <button
                onClick={() => scrollToSection('contact')}
                className={`relative px-2 py-1.5 text-sm font-normal transition-all duration-200 hover:text-[#D6794E] focus:outline-none focus:ring-2 focus:ring-[#D6794E] focus:ring-offset-2 focus:ring-offset-[#829BB8] rounded-xl ${
                  activeSection === 'contact'
                    ? 'text-[#D6794E]'
                    : 'text-[#FBF7F0]/90'
                }`}
                aria-current={activeSection === 'contact' ? 'page' : undefined}
              >
                Contact
                {activeSection === 'contact' && (
                  <span className="absolute -bottom-0.5 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-[#D6794E] rounded-full"></span>
                )}
              </button>
              
              {/* Book Session Button */}
              <button
                onClick={scrollToBooking}
                className="ml-3 px-4 py-1.5 bg-[#D6794E] text-white text-sm font-normal rounded-xl hover:bg-[#385B47] transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#D6794E] focus:ring-offset-2 focus:ring-offset-[#829BB8] whitespace-nowrap"
              >
                Book Session
              </button>
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-1.5 text-[#FBF7F0]/90 hover:text-[#D6794E] focus:outline-none focus:ring-2 focus:ring-[#D6794E] focus:ring-offset-2 focus:ring-offset-[#829BB8] rounded-md"
              aria-label="Toggle menu"
            >
              <div className="w-5 h-5 flex flex-col justify-center items-center">
                <span className={`block w-4 h-0.5 bg-current transition-all duration-300 ${isMobileMenuOpen ? 'rotate-45 translate-y-0.5' : '-translate-y-0.5'}`}></span>
                <span className={`block w-4 h-0.5 bg-current transition-all duration-300 ${isMobileMenuOpen ? 'opacity-0' : 'opacity-100'}`}></span>
                <span className={`block w-4 h-0.5 bg-current transition-all duration-300 ${isMobileMenuOpen ? '-rotate-45 -translate-y-0.5' : 'translate-y-0.5'}`}></span>
              </div>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Drawer */}
      {isMobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40 bg-[#385B47]/50 backdrop-blur-sm lg:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          ></div>
          
          {/* Drawer */}
          <div className="fixed top-0 right-0 z-50 w-80 h-full bg-[#385B47]/95 backdrop-blur-md shadow-2xl lg:hidden transform transition-transform duration-300">
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-[#3D5C4A]">
                <div>
                  <h2 className="text-lg font-normal cinematic-text-light">Menu</h2>
                </div>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-2 cinematic-text-light hover:text-white focus:outline-none focus:ring-2 focus:ring-[#D6794E] rounded-md"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>

              {/* Menu Items */}
              <nav className="flex-1 px-6 py-8 space-y-4" role="navigation">
                <button
                  onClick={() => scrollToSection('about')}
                  className={`w-full text-left px-4 py-3 rounded-xl text-base font-normal transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#D6794E] ${
                    activeSection === 'about'
                      ? 'bg-[#D6794E] text-white border-l-4 border-[#D6794E]'
                      : 'cinematic-text-light-secondary hover:text-white hover:bg-[#2F4C3B]/50'
                  }`}
                  aria-current={activeSection === 'about' ? 'page' : undefined}
                >
                  About
                </button>

                <button
                  onClick={() => scrollToSection('art-therapy')}
                  className={`w-full text-left px-4 py-3 rounded-xl text-base font-normal transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#D6794E] ${
                    activeSection === 'art-therapy'
                      ? 'bg-[#D6794E] text-white border-l-4 border-[#D6794E]'
                      : 'cinematic-text-light-secondary hover:text-white hover:bg-[#2F4C3B]/50'
                  }`}
                  aria-current={activeSection === 'art-therapy' ? 'page' : undefined}
                >
                  Art Therapy
                </button>

                <button
                  onClick={() => scrollToSection('testimonials')}
                  className={`w-full text-left px-4 py-3 rounded-xl text-base font-normal transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#D6794E] ${
                    activeSection === 'testimonials'
                      ? 'bg-[#D6794E] text-white border-l-4 border-[#D6794E]'
                      : 'cinematic-text-light-secondary hover:text-white hover:bg-[#2F4C3B]/50'
                  }`}
                  aria-current={activeSection === 'testimonials' ? 'page' : undefined}
                >
                  Testimonials
                </button>

                <button
                  onClick={() => scrollToSection('services-pricing')}
                  className={`w-full text-left px-4 py-3 rounded-xl text-base font-normal transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#D6794E] ${
                    activeSection === 'services-pricing'
                      ? 'bg-[#D6794E] text-white border-l-4 border-[#D6794E]'
                      : 'cinematic-text-light-secondary hover:text-white hover:bg-[#2F4C3B]/50'
                  }`}
                  aria-current={activeSection === 'services-pricing' ? 'page' : undefined}
                >
                  Services & Pricing
                </button>

                <button
                  onClick={() => scrollToSection('education')}
                  className={`w-full text-left px-4 py-3 rounded-xl text-base font-normal transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#D6794E] ${
                    activeSection === 'education'
                      ? 'bg-[#D6794E] text-white border-l-4 border-[#D6794E]'
                      : 'cinematic-text-light-secondary hover:text-white hover:bg-[#2F4C3B]/50'
                  }`}
                  aria-current={activeSection === 'education' ? 'page' : undefined}
                >
                  Education & Certification
                </button>

                <a
                  href="https://blog.yuvati.art"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full text-left px-4 py-3 rounded-xl text-base font-normal transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#D6794E] cinematic-text-light-secondary hover:text-white hover:bg-[#2F4C3B]/50 block"
                >
                  Blog
                </a>

                <button
                  onClick={() => scrollToSection('contact')}
                  className={`w-full text-left px-4 py-3 rounded-xl text-base font-normal transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#D6794E] ${
                    activeSection === 'contact'
                      ? 'bg-[#D6794E] text-white border-l-4 border-[#D6794E]'
                      : 'cinematic-text-light-secondary hover:text-white hover:bg-[#2F4C3B]/50'
                  }`}
                  aria-current={activeSection === 'contact' ? 'page' : undefined}
                >
                  Contact
                </button>
              </nav>

              {/* Book Session Button */}
              <div className="p-6 border-t border-[#3D5C4A]">
                <button
                  onClick={scrollToBooking}
                  className="w-full px-6 py-3 bg-[#D6794E] text-white text-base font-normal rounded-xl hover:bg-[#385B47] transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#D6794E] whitespace-nowrap"
                >
                  Book Session
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Spacer to prevent content from hiding under fixed header */}
      <div className="h-16 md:h-14"></div>
    </>
  );
}
